import axios from 'axios'
import router from '../router'
import { debugError } from '../utils/env'

// API基础配置
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api'

// 创建axios实例
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 2000, // 2秒超时
  headers: {
    'Content-Type': 'application/json'
  },
  // 自定义参数序列化：数组格式为 tags=value1&tags=value2（不使用 tags[] 格式）
  paramsSerializer: {
    serialize: (params) => {
      const parts: string[] = []
      for (const [key, value] of Object.entries(params)) {
        if (Array.isArray(value)) {
          // 数组：重复 key，如 tags=a&tags=b
          for (const v of value) {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`)
          }
        } else if (value !== null && value !== undefined) {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        }
      }
      return parts.join('&')
    }
  }
})

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 添加认证token
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // 记录请求开始时间（用于计算耗时）
    ;(config as any).metadata = { startTime: Date.now() }

    // 添加请求时间戳（防止缓存）
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now()
      }
    }

    return config
  },
  (error) => {
    debugError('请求配置错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    // 计算请求耗时
    const metadata = (response.config as any).metadata
    if (metadata?.startTime) {
      const duration = Date.now() - metadata.startTime
      const url = response.config.url
      const method = response.config.method?.toUpperCase()

      // 记录慢请求（超过 1 秒）
      if (duration > 1000) {
        debugError(`[慢请求] ${method} ${url} - 耗时: ${duration}ms`)
      } else {
        console.debug(`[API] ${method} ${url} - 耗时: ${duration}ms`)
      }

      // 超过 2 秒的警告
      if (duration > 2000) {
        debugError(`[警告] 请求超时 - ${method} ${url} - 耗时: ${duration}ms`)
      }
    }

    return response.data
  },
  (error) => {
    debugError('API请求错误:', error)

    // 计算请求耗时
    const metadata = (error.config as any)?.metadata
    if (metadata?.startTime) {
      const duration = Date.now() - metadata.startTime
      const url = error.config?.url
      const method = error.config?.method?.toUpperCase()
      debugError(`[错误] ${method} ${url} - 耗时: ${duration}ms - 错误: ${error.message}`)
    }

    // 处理网络错误
    if (!error.response) {
      // 检查是否是超时错误
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        throw new Error('请求超时，服务器响应时间过长（超过2秒）')
      }
      throw new Error('网络连接失败，请检查网络设置')
    }

    // 处理HTTP错误状态码
    const { status, data } = error.response

    // 优先使用后端返回的errormsg，其次使用error，最后使用默认消息
    const errorMsg = data?.errormsg || data?.error || ''

    switch (status) {
      case 400:
        throw new Error(errorMsg || '请求参数错误')
      case 401:
        // 清除本地token
        localStorage.removeItem('auth_token')
        localStorage.removeItem('user')
        // 跳转到首页（可以在首页的登录弹窗中进行登录）
        if (router.currentRoute.value.path !== '/') {
          router.push('/')
        }
        throw new Error(errorMsg || '登录已过期，请重新登录')
      case 403:
        throw new Error(errorMsg || '权限不足')
      case 404:
        throw new Error(errorMsg || '请求的资源不存在')
      case 408: // Request Timeout
        throw new Error(errorMsg || '请求超时，请稍后重试')
      case 429:
        throw new Error(errorMsg || '请求过于频繁，请稍后再试')
      case 500:
        throw new Error(errorMsg || '服务器内部错误')
      case 504: // Gateway Timeout
        throw new Error(errorMsg || '服务器响应超时')
      default:
        throw new Error(errorMsg || `请求失败 (${status})`)
    }
  }
)

// 通用API方法
export const apiGet = <T>(url: string, params?: any): Promise<T> => {
  return api.get(url, { params })
}

export const apiPost = <T>(url: string, data?: any): Promise<T> => {
  return api.post(url, data)
}

export const apiPut = <T>(url: string, data?: any): Promise<T> => {
  return api.put(url, data)
}

export const apiDelete = <T>(url: string): Promise<T> => {
  return api.delete(url)
}

// 认证相关API
export const authAPI = {
  // 发送验证码
  sendVerification: (email: string): Promise<any> => 
    api.post('/auth/send-verification', { email }),
  
  // 验证邮箱
  verifyEmail: (email: string, code: string): Promise<any> => 
    api.post('/auth/verify-email', { email, code }),
  
  // 获取当前用户信息
  getCurrentUser: (): Promise<any> => 
    api.get('/auth/me')
}

  // 纪录片相关API
export const documentaryAPI = {
  // 获取纪录片列表
  getDocumentaries: (params?: {
    page?: number;
    limit?: number;
    channelid?: number;
    category?: string;
    search?: string;
    sortBy?: string;
    year?: number;
    director?: string;
    minRating?: number;
    maxRating?: number;
    tags?: string[];
    genre?: string[];
  }): Promise<any> =>
    api.get('/documentaries', { params }),
  
  // 获取单个纪录片详情（支持 slug 或 id）
  getDocumentary: (identifier: string | number): Promise<any> => 
    api.get(`/documentaries/${identifier}`),
  
  // 获取纪录片弹幕
  getDanmaku: (identifier: string | number): Promise<any> => 
    api.get(`/documentaries/${identifier}/danmaku`),
  
  // 发送弹幕
  sendDanmaku: (identifier: string | number, data: { content: string; color?: string }): Promise<any> => 
    api.post(`/documentaries/${identifier}/danmaku`, data),

  // 获取评论列表
  getComments: (identifier: string | number, params?: { page?: number; limit?: number }): Promise<any> => 
    api.get(`/documentaries/${identifier}/comments`, { params }),
  
  // 发表评论
  createComment: (identifier: string | number, data: { content: string; parent_id?: number }): Promise<any> => 
    api.post(`/documentaries/${identifier}/comments`, data),
  
  // 获取用户评分
  getUserRating: (identifier: string | number): Promise<any> => 
    api.get(`/documentaries/${identifier}/rating`),
  
  // 评分并评论
  rateAndComment: (identifier: string | number, data: { rating: number; comment?: string }): Promise<any> => 
    api.post(`/documentaries/${identifier}/rate`, data),
  
  // 获取纪录片统计数据
  getDocumentaryStats: (identifier: string | number): Promise<any> =>
    api.get(`/documentaries/${identifier}/stats`),

  
  // 切换收藏状态
  toggleFavorite: (identifier: string | number): Promise<any> =>
    api.post(`/documentaries/${identifier}/favorite`),
  
  // 切换想看状态
  toggleWant: (identifier: string | number): Promise<any> =>
    api.post(`/documentaries/${identifier}/want`),
  
  // 取消想看状态
  cancelWant: (identifier: string | number): Promise<any> =>
    api.delete(`/documentaries/${identifier}/want`),
  
  // 切换看过状态
  toggleWatched: (identifier: string | number): Promise<any> =>
    api.post(`/documentaries/${identifier}/watched`),
  
  // 取消看过状态
  cancelWatched: (identifier: string | number): Promise<any> =>
    api.delete(`/documentaries/${identifier}/watched`),
  
  // 切换点赞状态
  toggleLike: (identifier: string | number): Promise<any> =>
    api.post(`/documentaries/${identifier}/like`),
  
  // 删除评论
  deleteComment: (commentId: number): Promise<any> => 
    api.delete(`/comments/${commentId}`),

  // 获取评论全文（仅限已验证用户）
  getCommentFullContent: (commentId: number): Promise<any> =>
    api.get(`/comments/${commentId}/full`)
}

// 频道相关API
export const channelAPI = {
  // 获取所有频道
  getChannels: () => 
    apiGet('/tvchannels'),
  
  // 获取单个频道信息
  getChannel: (slug: string) => 
    apiGet(`/tvchannels/${slug}`)
}

// 收录申请相关API
export const submissionAPI = {
  // 提交收录申请
  createSubmission: (data: {
    title: string
    director: string
    year: number
    duration?: number
    country?: string
    description: string
    tv_channel_id: number
    platform_id?: number
    video_url?: string
    submitter_name?: string
    submitter_email: string
    submit_reason?: string
    tags?: string[]
  }): Promise<any> =>
    api.post('/submissions', data)
}

// 推送通知相关API
export const pushAPI = {
  // 订阅推送
  subscribe: (userId: number, subscription: {
    endpoint: string
    keys: {
      p256dh: string
      auth: string
    }
  }): Promise<any> =>
    api.post('/push/subscribe', {
      user_id: userId,
      endpoint: subscription.endpoint,
      keys: subscription.keys
    }),

  // 取消订阅
  unsubscribe: (userId: number): Promise<any> =>
    api.delete(`/push/unsubscribe/${userId}`),

  // 获取 VAPID 公钥
  getVAPIDKey: (): Promise<any> =>
    api.post('/push/test', {}),

  // 发送测试通知
  sendTest: (data: {
    title: string
    body: string
    icon?: string
  }): Promise<any> =>
    api.post('/push/send', data)
}

// 用户相关API
export const userAPI = {
  // 获取用户资料
  getProfile: (): Promise<any> =>
    api.get('/user/profile'),

  // 获取用户收藏列表
  getFavorites: (page = 1, limit = 20): Promise<any> =>
    api.get('/user/favorites', { params: { page, limit } }),

  // 获取用户评分记录
  getRatings: (page = 1, limit = 20): Promise<any> =>
    api.get('/user/ratings', { params: { page, limit } }),

  // 获取用户想看列表
  getWatchlists: (page = 1, limit = 20): Promise<any> =>
    api.get('/user/watchlists', { params: { page, limit } }),

  // 获取用户看过列表
  getWatcheds: (page = 1, limit = 20): Promise<any> =>
    api.get('/user/watcheds', { params: { page, limit } }),

  // 获取用户点赞列表
  getLikes: (page = 1, limit = 20): Promise<any> =>
    api.get('/user/likes', { params: { page, limit } }),

  // 修改密码
  updatePassword: (oldPassword: string, newPassword: string): Promise<any> =>
    api.post('/user/password', { old_password: oldPassword, new_password: newPassword })
}

export default api